import isEqual from 'lodash/isEqual';
import {
  mergeSaleOrderDetails,
  type SaleOrderDetails,
  SaleOrderDetailsSchema,
} from '@am3nd-org/pointcraft-types';

import { CONFIG } from 'src/environments/environment';

export interface OrderDetailsScope {
  businessId: string;
  sourceCheckoutId: string;
  canonicalSaleId?: string | null;
}

interface MetadataRecord extends OrderDetailsScope {
  canonicalSaleId: string | null;
  details: SaleOrderDetails;
  pending: SaleOrderDetails | null;
  flight: { patch: SaleOrderDetails; revision: number } | null;
  conflict: boolean;
  acknowledgedAt?: number;
}

interface MetadataResponse {
  saleId: string;
  orderDetails: SaleOrderDetails | null;
  orderDetailsRevision: number;
}

interface SyncDependencies {
  storage: () => Storage;
  environment: string;
  read: (saleId: string) => Promise<MetadataResponse>;
  patch: (saleId: string, patch: SaleOrderDetails, revision: number) => Promise<MetadataResponse>;
  isCurrentBusiness: (businessId: string) => boolean;
}

/** Independent of payment execution. Every dispatch and acknowledgement is durable.
 * One record per source sale survives journal resets; CAS prevents stale network
 * requests from winning after another sender has already saved a newer edit.
 */
export function createOrderDetailsSync(deps: SyncDependencies) {
  const prefix = `pointcraft.orderDetails.v1:${deps.environment}:`;
  const running = new Map<string, Promise<void>>();
  const aliasKey = (scope: OrderDetailsScope) =>
    `${prefix}binding:${scope.businessId}:${scope.sourceCheckoutId}`;
  const keyFor = (scope: OrderDetailsScope) =>
    `${prefix}${scope.businessId}:${
      scope.canonicalSaleId ?? deps.storage().getItem(aliasKey(scope)) ?? scope.sourceCheckoutId
    }`;
  const load = (scope: OrderDetailsScope): MetadataRecord | null => {
    const raw = deps.storage().getItem(keyFor(scope));
    if (!raw) return null;
    const record = JSON.parse(raw) as MetadataRecord;
    if (
      record.businessId !== scope.businessId ||
      (record.sourceCheckoutId !== scope.sourceCheckoutId &&
        record.canonicalSaleId !== scope.sourceCheckoutId)
    )
      throw new Error('Order details scope mismatch.');
    SaleOrderDetailsSchema.parse(record.details);
    if (record.pending) SaleOrderDetailsSchema.parse(record.pending);
    if (record.flight) SaleOrderDetailsSchema.parse(record.flight.patch);
    return record;
  };
  const write = (record: MetadataRecord) => {
    // A failed durable write must reach the caller; never claim a saved edit.
    deps.storage().setItem(keyFor(record), JSON.stringify(record));
  };
  const initial = (scope: OrderDetailsScope): MetadataRecord => ({
    ...scope,
    canonicalSaleId: scope.canonicalSaleId ?? null,
    details: {},
    pending: null,
    flight: null,
    conflict: false,
  });
  const enqueue = (scope: OrderDetailsScope, patch: SaleOrderDetails) => {
    const record =
      load(scope) ??
      initial({
        ...scope,
        canonicalSaleId: scope.canonicalSaleId ?? deps.storage().getItem(aliasKey(scope)),
      });
    const parsed = SaleOrderDetailsSchema.parse(patch);
    const details = mergeSaleOrderDetails(record.details, parsed) ?? {};
    if (isEqual(details, record.details) && !record.conflict) return;
    write({
      ...record,
      details,
      acknowledgedAt: undefined,
      ...(record.conflict ? { conflict: false, flight: null } : {}),
      pending: mergeSaleOrderDetails(record.pending, parsed) ?? null,
    });
  };
  const bind = (scope: OrderDetailsScope, canonicalSaleId: string) => {
    const record =
      load({ ...scope, canonicalSaleId: null }) ?? initial({ ...scope, canonicalSaleId: null });
    if (record.canonicalSaleId === canonicalSaleId) return;
    const existing = load({ ...scope, canonicalSaleId });
    if (!existing)
      write(
        record.canonicalSaleId
          ? { ...initial(scope), canonicalSaleId, details: record.details }
          : { ...record, canonicalSaleId }
      );
    // Old canonical records keep their pending work if this cart is replaced.
    deps.storage().setItem(aliasKey(scope), canonicalSaleId);
    if (scope.sourceCheckoutId !== canonicalSaleId)
      deps.storage().removeItem(`${prefix}${scope.businessId}:${scope.sourceCheckoutId}`);
  };
  const flushRecord = async (scope: OrderDetailsScope) => {
    let record = load(scope);
    while (record?.canonicalSaleId && deps.isCurrentBusiness(scope.businessId)) {
      if (record.conflict || (!record.pending && !record.flight)) return;
      const saleId = record.canonicalSaleId;
      if (!record.flight) {
        // 404 is retained until the completed-sale report has been ingested.
        // eslint-disable-next-line no-await-in-loop
        const current = await deps.read(saleId);
        if (!deps.isCurrentBusiness(scope.businessId)) return;
        record = load(scope)!;
        if (record.flight) return; // Another app instance already owns the dispatch.
        if (!record.pending) return;
        write({
          ...record,
          pending: null,
          flight: {
            patch: record.pending,
            revision: current.orderDetailsRevision,
          },
        });
        record = load(scope)!;
      }
      const flight = record.flight!;
      let response: MetadataResponse;
      try {
        if (!deps.isCurrentBusiness(scope.businessId)) return;
        // eslint-disable-next-line no-await-in-loop
        response = await deps.patch(saleId, flight.patch, flight.revision);
      } catch (error) {
        if ((error as { response?: { status?: number } }).response?.status === 409) {
          // A lost acknowledgement is safe to acknowledge only when every
          // submitted field still matches. Never rebase a stale edit blindly.
          // eslint-disable-next-line no-await-in-loop
          const current = await deps.read(saleId);
          if (
            !Object.entries(flight.patch).every(([key, value]) =>
              isEqual(current.orderDetails?.[key], value)
            )
          ) {
            const latest = load(scope)!;
            if (isEqual(latest.flight, flight)) write({ ...latest, conflict: true });
            return;
          }
          response = current;
        } else throw error;
      }
      if (response.saleId !== saleId) throw new Error('Order details acknowledgement mismatch.');
      record = load(scope)!;
      // A second app instance may already have acknowledged this flight.
      if (!isEqual(record.flight, flight)) return;
      // Edits made during dispatch remain pending and are sent next.
      write({
        ...record,
        flight: null,
        acknowledgedAt: record.pending ? undefined : Date.now(),
        details: mergeSaleOrderDetails(response.orderDetails, record.pending) ?? {},
      });
      record = load(scope);
    }
  };
  const flush = async (businessId: string) => {
    const storage = deps.storage();
    const scopes = Array.from({ length: storage.length }, (_, index) => storage.key(index))
      .filter((key): key is string => Boolean(key?.startsWith(`${prefix}${businessId}:`)))
      .map((key) => JSON.parse(storage.getItem(key)!) as MetadataRecord);
    await Promise.all(
      scopes.map((scope) => {
        const key = keyFor(scope);
        if (!running.has(key)) {
          running.set(
            key,
            flushRecord(scope).finally(() => running.delete(key))
          );
        }
        return running.get(key);
      })
    );
    // Keep only recent acknowledged data; never evict pending/conflicted edits.
    // Small source→sale bindings remain so subsequent edits still target the same sale.
    scopes
      .map((scope) => load(scope))
      .filter((record): record is MetadataRecord =>
        Boolean(record?.acknowledgedAt && !record.pending && !record.flight && !record.conflict)
      )
      .sort((a, b) => b.acknowledgedAt! - a.acknowledgedAt!)
      .slice(20)
      .forEach((record) => storage.removeItem(keyFor(record)));
  };
  /**
   * Pull the canonical sale's saved metadata (edits made on another device)
   * into a clean local record. Pending, in-flight, or conflicted local edits
   * are never overwritten; a sale that is not ingested yet keeps local data.
   */
  const refresh = async (scope: OrderDetailsScope): Promise<SaleOrderDetails | null> => {
    const record = load(scope);
    if (
      !record?.canonicalSaleId ||
      record.pending ||
      record.flight ||
      record.conflict ||
      !deps.isCurrentBusiness(scope.businessId)
    )
      return record?.details ?? null;
    let current: MetadataResponse;
    try {
      current = await deps.read(record.canonicalSaleId);
    } catch (error) {
      if ((error as { response?: { status?: number } }).response?.status === 404)
        return record.details;
      throw error;
    }
    const latest = load(scope);
    if (!latest || latest.pending || latest.flight || latest.conflict)
      return latest?.details ?? null;
    const details = current.orderDetails ?? {};
    if (!isEqual(details, latest.details))
      write({ ...latest, details, acknowledgedAt: latest.acknowledgedAt ?? Date.now() });
    return details;
  };
  const forgetAcknowledged = (scope: OrderDetailsScope): boolean => {
    const record = load(scope);
    if (!record) return true;
    if (
      !record.acknowledgedAt ||
      record.pending ||
      record.flight ||
      record.conflict ||
      running.has(keyFor(scope))
    )
      return false;
    deps.storage().removeItem(keyFor(scope));
    return deps.storage().getItem(keyFor(scope)) === null;
  };
  return { enqueue, bind, flush, refresh, forgetAcknowledged, get: load };
}

// The runtime owns API/auth wiring; durable storage must not import reset code.
type RuntimeDependencies = Pick<SyncDependencies, 'isCurrentBusiness' | 'read' | 'patch'>;
let runtime: RuntimeDependencies | null = null;
export function configureSaleOrderDetailsSync(dependencies: RuntimeDependencies | null) {
  runtime = dependencies;
}

export const saleOrderDetailsSync = createOrderDetailsSync({
  environment: CONFIG.environment,
  storage: () => localStorage,
  isCurrentBusiness: (businessId) => runtime?.isCurrentBusiness(businessId) ?? false,
  read: async (saleId) => {
    if (!runtime) throw new Error('Order details sync is not active.');
    return runtime.read(saleId);
  },
  patch: async (saleId, patch, revision) => {
    if (!runtime) throw new Error('Order details sync is not active.');
    return runtime.patch(saleId, patch, revision);
  },
});
